import { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { CheckCircle, XCircle, ArrowRight, ChevronRight, Shield, Clock, Star, Zap } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { LANDING_PAGES } from '@/data/keywordLandingContent';
import MobileMenu from '@/components/MobileMenu';

const SITE = 'https://flexaviral.com';

function Stars({ value }) {
  return (
    <span className="flex items-center gap-0.5" aria-label={`${value} bintang`}>
      {[1, 2, 3, 4, 5].map(i => (
        <Star key={i} className={`h-3.5 w-3.5 ${i <= Math.round(value) ? 'fill-amber-400 text-amber-400' : 'text-zinc-200'}`} />
      ))}
    </span>
  );
}

function ComparisonTable({ page }) {
  const rows = [
    { label: `Harga / 1K ${page.unit}`, flexa: `Dari ${page.priceFrom}`, reseller: '3-5x lebih mahal', panel: 'Murah tapi USD' },
    { label: 'Kelajuan delivery', flexa: 'Auto, mula 60 saat', reseller: 'Manual, tunggu seller reply', panel: 'Auto' },
    { label: 'Password diperlukan?', flexa: 'Tidak (link sahaja)', reseller: 'Kadang-kadang diminta ⚠️', panel: 'Tidak' },
    { label: 'Refill guarantee', flexa: 'Ya, satu klik dari dashboard', reseller: 'Ikut mood seller', panel: 'Perlu ticket English' },
    { label: 'Bayaran FPX / TnG eWallet', flexa: 'Ya, semua bank MY', reseller: 'Ya', panel: 'Tiada (kad/crypto sahaja)' },
    { label: 'Support Bahasa Melayu', flexa: 'Ya, live chat', reseller: 'Ya', panel: 'Tiada' },
    { label: 'AI tools & bonus kredit', flexa: 'Percuma untuk semua member', reseller: 'Tiada', panel: 'Tiada' },
  ];
  return (
    <div className="overflow-x-auto rounded-xl border border-zinc-200" data-testid="comparison-table">
      <table className="w-full text-sm min-w-[640px]">
        <thead>
          <tr className="bg-zinc-50 border-b border-zinc-200">
            <th className="text-left py-3 px-4 text-xs uppercase text-zinc-500 font-medium">Perbandingan</th>
            <th className="text-left py-3 px-4 text-xs uppercase font-bold text-violet-700 bg-violet-50">Flexa 🇲🇾</th>
            <th className="text-left py-3 px-4 text-xs uppercase text-zinc-500 font-medium">Reseller Shopee/IG</th>
            <th className="text-left py-3 px-4 text-xs uppercase text-zinc-500 font-medium">Panel Luar Negara</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-zinc-100 last:border-0">
              <td className="py-3 px-4 font-medium text-zinc-700">{r.label}</td>
              <td className="py-3 px-4 bg-violet-50/50 font-semibold text-zinc-900">
                <span className="flex items-start gap-1.5"><CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />{r.flexa}</span>
              </td>
              <td className="py-3 px-4 text-zinc-500">
                <span className="flex items-start gap-1.5"><XCircle className="h-4 w-4 text-zinc-300 flex-shrink-0 mt-0.5" />{r.reseller}</span>
              </td>
              <td className="py-3 px-4 text-zinc-500">
                <span className="flex items-start gap-1.5"><XCircle className="h-4 w-4 text-zinc-300 flex-shrink-0 mt-0.5" />{r.panel}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function KeywordLandingPage() {
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\/+|\/+$/g, '');
  const page = LANDING_PAGES[slug];

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!page) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center px-4">
        <p className="text-lg font-bold text-zinc-900 mb-2">Halaman tidak dijumpai</p>
        <Link to="/smm-panel-malaysia" className="text-violet-600 hover:underline">Kembali ke SMM Panel Malaysia</Link>
      </div>
    );
  }

  const url = `${SITE}/${slug}`;
  const priceNum = page.priceFrom.replace('RM', '');

  return (
    <div className="min-h-screen bg-white" data-testid={`landing-${slug}`}>
      <Helmet>
        <title>{page.title}</title>
        <meta name="description" content={page.metaDescription} />
        <link rel="canonical" href={url} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={page.title} />
        <meta property="og:description" content={page.metaDescription} />
        <meta property="og:url" content={url} />
        <meta property="og:image" content={`${SITE}/og-image.jpg`} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          "name": `${page.keyword} Malaysia`,
          "description": page.metaDescription,
          "image": `${SITE}/og-image.jpg`,
          "brand": { "@type": "Brand", "name": "Flexa" },
          "offers": {
            "@type": "AggregateOffer",
            "priceCurrency": "MYR",
            "lowPrice": priceNum,
            "offerCount": page.serviceCount,
            "availability": "https://schema.org/InStock",
            "url": url
          },
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": page.rating.value,
            "reviewCount": page.rating.count,
            "bestRating": 5
          },
          "review": page.testimonials.map(t => ({
            "@type": "Review",
            "author": { "@type": "Person", "name": t.name },
            "reviewRating": { "@type": "Rating", "ratingValue": t.rating, "bestRating": 5 },
            "reviewBody": t.text
          }))
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Flexa", "item": SITE },
            { "@type": "ListItem", "position": 2, "name": "SMM Panel Malaysia", "item": `${SITE}/smm-panel-malaysia` },
            { "@type": "ListItem", "position": 3, "name": `${page.keyword} Malaysia`, "item": url }
          ]
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": page.faqs.map(f => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a }
          }))
        })}</script>
      </Helmet>

      {/* Nav */}
      <nav className="bg-white border-b border-zinc-100 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-between h-14">
          <Link to="/" className="flex items-center gap-2">
            <img src="/flexa-wordmark.png" alt="Flexa" className="h-6" />
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/smm-panel-malaysia" className="text-sm text-zinc-500 hover:text-zinc-900 hidden sm:inline">SMM Panel</Link>
            <Link to="/services" className="text-sm text-zinc-500 hover:text-zinc-900 hidden sm:inline">Semua Servis</Link>
            <Link to="/signup" data-testid="landing-nav-signup" className="h-9 px-4 rounded-full bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 flex items-center gap-1.5">
              Daftar <ArrowRight className="h-3.5 w-3.5" />
            </Link>
            <MobileMenu className="sm:hidden" />
          </div>
        </div>
      </nav>

      {/* Breadcrumb */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3">
        <div className="flex items-center gap-1.5 text-xs text-zinc-400">
          <Link to="/" className="hover:text-violet-600">Flexa</Link>
          <ChevronRight className="h-3 w-3" />
          <Link to="/smm-panel-malaysia" className="hover:text-violet-600">SMM Panel Malaysia</Link>
          <ChevronRight className="h-3 w-3" />
          <span className="text-zinc-700 font-medium">{page.keyword}</span>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-gradient-to-b from-violet-50 to-white py-12 sm:py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-1.5 bg-white border border-violet-200 rounded-full px-3 py-1 text-xs font-semibold text-violet-700 mb-4">
            <Stars value={page.rating.value} /> {page.rating.value}/5 dari {page.rating.count}+ pelanggan Malaysia
          </div>
          <h1 className="text-2xl sm:text-4xl lg:text-5xl font-extrabold text-zinc-900 tracking-tight mb-4" data-testid="landing-h1">
            {page.h1}
          </h1>
          <p className="text-base text-zinc-600 max-w-2xl mx-auto mb-6">{page.heroTagline}</p>
          <div className="flex items-center justify-center gap-4 text-sm text-zinc-500 mb-8 flex-wrap">
            <span className="flex items-center gap-1.5"><Zap className="h-4 w-4 text-amber-500" /> Dari <strong className="text-zinc-900">{page.priceFrom}/1K</strong></span>
            <span className="flex items-center gap-1.5"><Clock className="h-4 w-4 text-blue-500" /> Delivery 60 saat</span>
            <span className="flex items-center gap-1.5"><Shield className="h-4 w-4 text-violet-500" /> Tanpa password</span>
          </div>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <Link to="/signup" data-testid="landing-hero-cta" className="inline-flex items-center gap-2 h-11 px-6 rounded-full bg-violet-600 text-white font-bold hover:bg-violet-700">
              Daftar Percuma Sekarang <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to={`/beli/${page.categorySlug}`} className="inline-flex items-center gap-2 h-11 px-6 rounded-full border border-zinc-300 text-zinc-700 font-semibold hover:border-violet-400 hover:text-violet-700">
              Lihat Senarai Harga
            </Link>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-10 px-4">
        <div className="max-w-3xl mx-auto space-y-4">
          {page.intro.map((p, i) => (
            <p key={i} className="text-[15px] text-zinc-600 leading-relaxed">{p}</p>
          ))}
        </div>
      </section>

      {/* Benefits */}
      <section className="py-10 px-4 bg-zinc-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-xl sm:text-2xl font-bold text-zinc-900 mb-6 text-center">Kenapa {page.keyword} di Flexa?</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {page.benefits.map((b, i) => (
              <div key={i} className="p-5 bg-white rounded-xl border border-zinc-200">
                <div className="h-9 w-9 rounded-lg bg-violet-50 flex items-center justify-center mb-3">
                  <CheckCircle className="h-4 w-4 text-violet-600" />
                </div>
                <h3 className="font-bold text-zinc-900 text-sm mb-1">{b.title}</h3>
                <p className="text-xs text-zinc-500 leading-relaxed">{b.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Long-form sections */}
      {page.sections.map((sec, i) => (
        <section key={i} className={`py-10 px-4 ${i % 2 === 1 ? 'bg-zinc-50' : ''}`}>
          <div className="max-w-3xl mx-auto">
            <h2 className="text-xl sm:text-2xl font-bold text-zinc-900 mb-4">{sec.heading}</h2>
            <div className="space-y-4">
              {sec.paras.map((p, j) => (
                <p key={j} className="text-[15px] text-zinc-600 leading-relaxed">{p}</p>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* Comparison table */}
      <section className="py-10 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-xl sm:text-2xl font-bold text-zinc-900 mb-2 text-center">Flexa vs Reseller vs Panel Luar Negara</h2>
          <p className="text-sm text-zinc-500 text-center mb-6">Perbandingan jujur sebelum anda {page.keyword.toLowerCase()} di mana-mana.</p>
          <ComparisonTable page={page} />
        </div>
      </section>

      {/* How to order */}
      <section className="py-10 px-4 bg-zinc-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-xl sm:text-2xl font-bold text-zinc-900 mb-6 text-center">Cara {page.keyword} di Flexa (4 Langkah)</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {page.steps.map((s, i) => (
              <div key={i} className="p-5 bg-white rounded-xl border border-zinc-200 relative">
                <div className="h-8 w-8 rounded-full bg-violet-600 text-white text-sm font-bold flex items-center justify-center mb-3">{i + 1}</div>
                <h3 className="font-bold text-zinc-900 text-sm mb-1">{s.title}</h3>
                <p className="text-xs text-zinc-500 leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/signup" className="inline-flex items-center gap-2 h-11 px-6 rounded-full bg-violet-600 text-white font-bold hover:bg-violet-700">
              Mula Sekarang - Percuma <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-10 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-xl sm:text-2xl font-bold text-zinc-900 mb-6 text-center">Apa Kata Pelanggan Malaysia</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {page.testimonials.map((t, i) => (
              <div key={i} className="p-5 bg-white rounded-xl border border-zinc-200">
                <Stars value={t.rating} />
                <p className="text-sm text-zinc-600 leading-relaxed mt-3 mb-4">"{t.text}"</p>
                <p className="text-xs font-bold text-zinc-900">{t.name} <span className="font-normal text-zinc-400">- {t.location}</span></p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-10 px-4 bg-zinc-50">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-xl sm:text-2xl font-bold text-zinc-900 mb-6 text-center">Soalan Lazim - {page.keyword} Malaysia</h2>
          <Accordion type="single" collapsible className="space-y-2" data-testid="landing-faq">
            {page.faqs.map((faq, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border border-zinc-200 rounded-xl px-5 bg-white">
                <AccordionTrigger className="text-sm font-semibold text-zinc-900 hover:no-underline py-4 text-left">{faq.q}</AccordionTrigger>
                <AccordionContent className="text-sm text-zinc-600 leading-relaxed pb-4">{faq.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Internal links */}
      <section className="py-10 px-4 border-t border-zinc-100">
        <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <h2 className="text-sm font-bold text-zinc-900 mb-3">Servis Lain Yang Popular</h2>
            <div className="flex flex-col gap-2">
              {page.related.map(r => (
                <Link key={r.slug} to={`/${r.slug}`} className="flex items-center gap-2 text-sm font-medium text-violet-700 hover:underline">
                  <ChevronRight className="h-3.5 w-3.5" /> {r.label}
                </Link>
              ))}
              <Link to={`/beli/${page.categorySlug}`} className="flex items-center gap-2 text-sm font-medium text-violet-700 hover:underline">
                <ChevronRight className="h-3.5 w-3.5" /> Senarai penuh harga {page.keyword.toLowerCase()}
              </Link>
              <Link to="/smm-panel-malaysia" className="flex items-center gap-2 text-sm font-medium text-violet-700 hover:underline">
                <ChevronRight className="h-3.5 w-3.5" /> SMM Panel Malaysia - Semua Platform
              </Link>
            </div>
          </div>
          <div>
            <h2 className="text-sm font-bold text-zinc-900 mb-3">Panduan Berkaitan</h2>
            <div className="flex flex-col gap-2">
              {page.blogLinks.map(b => (
                <Link key={b.slug} to={`/blog/${b.slug}`} className="flex items-center gap-2 text-sm text-zinc-600 hover:text-violet-700 hover:underline">
                  <ChevronRight className="h-3.5 w-3.5 text-violet-400" /> {b.title}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 px-4 bg-violet-600 text-white text-center">
        <h2 className="text-xl sm:text-2xl font-bold mb-3">Sedia Nak {page.keyword}?</h2>
        <p className="text-violet-200 mb-6">Daftar percuma. Harga dari {page.priceFrom}/1K {page.unit}.</p>
        <Link to="/signup" data-testid="landing-footer-cta" className="inline-flex items-center gap-2 h-11 px-6 rounded-full bg-white text-violet-700 font-bold hover:bg-violet-50">
          Daftar Percuma Sekarang <ArrowRight className="h-4 w-4" />
        </Link>
      </section>

      {/* Footer */}
      <footer className="py-6 px-4 bg-zinc-900 text-center">
        <p className="text-xs text-zinc-500">&copy; {new Date().getFullYear()} Flexa (flexaviral.com)</p>
        <p className="text-[10px] text-zinc-600 mt-1 tracking-wider uppercase">Powered by <span className="text-zinc-400 font-semibold">UNDERADS</span></p>
      </footer>
    </div>
  );
}
